import React, { FC, useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Card, Button } from 'src/common';
import { useUpdateMpName, getUpdateCacheOnUpdateMpName, useLoadMp } from 'src/models/matchingProfiles/hooks';
import styles from './index.module.scss';
import OffsetAnchor from '../OffsetAnchor';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-light-svg-icons';
interface IFormState {
  name: string;
}

export const MatchingProfileName: FC = () => {
  const { t } = useTranslation();
  const [updateMpName] = useUpdateMpName();

  const formRef = useRef(null);

  const [renameVisible, setRenameVisible] = useState(false);
  const { data: mp, loading } = useLoadMp();
  const mpName = mp?.name;

  const [form] = Form.useForm();

  const initialValues = useMemo((): IFormState => {
    return {
      name: mpName || ''
    };
  }, [mpName]);

  useEffect(() => {
    if (formRef.current) {
      !!form &&
        form.setFieldsValue({
          name: mpName || ''
        });
    }
  }, [form, mpName]);

  const hideRename = useCallback(() => {
    setRenameVisible(false);
    form.resetFields();
  }, [form]);

  const showRename = useCallback(() => {
    setRenameVisible(true);
    form.setFieldsValue({ name: mpName });
  }, [form, mpName]);

  const onSubmit = useCallback(
    (values: IFormState) => {
      const { name } = values;
      mp &&
        updateMpName({
          variables: { name, id: mp.id },
          update: getUpdateCacheOnUpdateMpName(mp.id),
          optimisticResponse: {
            __typename: 'Mutation',
            updateMatchingProfile: {
              __typename: 'MatchingProfile',
              ...mp,
              name
            }
          }
        })
          .then(() => {
            hideRename();
          })
          .catch(error => {
            form.setFields([
              {
                name: 'name',
                errors: [error.message]
              }
            ]);
          });
    },
    [mp, updateMpName, hideRename, form]
  );

  return (
    <Card
      title={
        <>
          {mp && (
            <div className={styles.titleContainer}>
              <OffsetAnchor id={'matchingProfiles'} />
              <div className={styles.title}>{mp.name || t('Tenders.matchingProfileDefaultName')}</div>
              {mp.accessToEdit && (
                <ul className={styles.navActions}>
                  <li className={styles.navAction}>
                    <Button
                      type={'text'}
                      onClick={renameVisible ? hideRename : showRename}
                      icon={<FontAwesomeIcon icon={faEdit} />}
                    />
                  </li>
                </ul>
              )}
            </div>
          )}
        </>
      }
      loading={loading}
      custom={true}
      className={styles.preferencesName}
    >
      {mp && (
        <>
          {renameVisible && (
            <Form
              className={classNames(styles.form, styles.updateNameForm)}
              onFinish={onSubmit}
              initialValues={initialValues}
              form={form}
              layout={'vertical'}
              ref={formRef}
            >
              <Form.Item
                label={t('Common.name')}
                name={'name'}
                rules={[
                  {
                    required: true,
                    message: t('Common.inputRequired', { inputName: t('Tenders.monitoringProfile') })
                  },
                  { max: 30, message: t('Common.inputTooLong', { inputName: t('Tenders.monitoringProfile') }) }
                ]}
              >
                <Input data-type={'name'} type={'text'} placeholder={t('Common.name')} />
              </Form.Item>
              <div className={styles.formFooter}>
                <Button type={'primary'} htmlType={'submit'}>
                  {t('Common.rename')}
                </Button>
              </div>
            </Form>
          )}
        </>
      )}
    </Card>
  );
};

export default MatchingProfileName;
